import { css } from 'aphrodite';
import * as React from 'react';
import { CsmMeetingTypes, IAccount, IUser, NormalizedStages } from '../../../extViewmodels';
import { getDisplayName } from '../../../models/NameUtils';
import { IRichContentEditorState } from '../../../viewmodels/AppViewModels';
import { DayPicker } from '../../../web/components/DayPicker';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
} from '../../../web/components-v2/Combobox';
import { ComboboxAvatarItem } from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { titles } from '../../../web/styles/colors';
import { styleSheet } from './styles';

const MEETING_NOTE_EDITOR_CLASS = 'meeting-note-editor-flow';

const EditorCSS = `
	body {
		background: transparent;
		color: ${titles};
		font-family: "Sora", sans-serif;
		font-size: 14px;
		margin: 0;
		padding: 16px 20px;
	}
`;

interface IProps {
	// Form values
	selectedAccount: IAccount | null;
	accountSearchValue: string;
	accounts: IAccount[];
	accountsQueryLoading: boolean;
	meetingNoteContentState: IRichContentEditorState | null;
	meetingType: CsmMeetingTypes | null;
	meetingStatus: string;
	accountStage: string | null;
	duration: number;
	isOnboarding: boolean;
	nextMeetingDate: Date | null;
	showNextMeetingDatePicker: boolean;
	validationErrors: string[];
	isSavingNote: boolean;
	csmUserForCcNotice: IUser | null;
	// Handlers
	onAccountChange: (account: IAccount | null) => void;
	onAccountSearchValueChange: (value: string) => void;
	onClearAccount: () => void;
	onContentStateChanged: (state: IRichContentEditorState | null) => void;
	onMeetingTypeChange: (type: CsmMeetingTypes | null) => void;
	onMeetingStatusChange: (status: string) => void;
	onAccountStageChange: (stage: string | null) => void;
	onDurationChange: (duration: number) => void;
	onIsOnboardingChange: (checked: boolean) => void;
	onNextMeetingDateChange: (date: Date | null) => void;
	onShowNextMeetingDatePickerChange: (show: boolean) => void;
	onSave: () => void;
	onSkip: () => void;
}

export const MeetingNoteForm: React.FC<IProps> = ({
	selectedAccount,
	accountSearchValue,
	accounts,
	accountsQueryLoading,
	meetingNoteContentState,
	meetingType,
	meetingStatus,
	accountStage,
	duration,
	isOnboarding,
	nextMeetingDate,
	showNextMeetingDatePicker,
	validationErrors,
	isSavingNote,
	csmUserForCcNotice,
	onAccountChange,
	onAccountSearchValueChange,
	onClearAccount,
	onContentStateChanged,
	onMeetingTypeChange,
	onMeetingStatusChange,
	onAccountStageChange,
	onDurationChange,
	onIsOnboardingChange,
	onNextMeetingDateChange,
	onShowNextMeetingDatePickerChange,
	onSave,
	onSkip,
}) => {
	// Inject scoped toolbar border style
	React.useEffect(() => {
		const style = document.createElement('style');
		style.textContent = `.${MEETING_NOTE_EDITOR_CLASS} .tox-toolbar__primary { border-bottom: 1px solid #D8D8D8 !important; }`;
		document.head.appendChild(style);
		return () => {
			document.head.removeChild(style);
		};
	}, []);

	return (
		<div className={css(styleSheet.meetingNoteForm)}>
			{/* Validation Errors */}
			{validationErrors.length > 0 && (
				<div className={css(styleSheet.validationErrors)}>
					{validationErrors.map((error, idx) => (
						<div key={idx} className={css(styleSheet.validationError)}>
							{error}
						</div>
					))}
				</div>
			)}

			{/* Two Column Layout */}
			<div className={css(styleSheet.formColumns)}>
				{/* Left Column */}
				<div className={css(styleSheet.formLeftColumn)}>
					{/* Account Selection/Display */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>
							Account <span className={css(styleSheet.required)}>*</span>
						</label>
						{selectedAccount ? (
							<div className={css(styleSheet.selectedAccountDisplay)}>
								<span>{selectedAccount.companyName}</span>
								<button className={css(styleSheet.changeAccountButton)} onClick={onClearAccount}>
									Change
								</button>
							</div>
						) : (
							<Combobox
								items={accounts}
								value={selectedAccount}
								onValueChange={(account: IAccount | null) => {
									onAccountChange(account);
								}}
								inputValue={accountSearchValue}
								onInputValueChange={onAccountSearchValueChange}
								itemToStringLabel={(account: IAccount) => account.companyName || ''}
								isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
							>
								<ComboboxInput placeholder='Search for account...' />
								<ComboboxContent>
									<ComboboxStatus>
										{accountsQueryLoading
											? 'Searching...'
											: accounts.length === 0 && accountSearchValue
												? `No accounts found for "${accountSearchValue}"`
												: accounts.length > 0
													? `${accounts.length} account${accounts.length === 1 ? '' : 's'} found`
													: 'Start typing to search accounts...'}
									</ComboboxStatus>
									<ComboboxList>
										{(account: IAccount) => (
											<ComboboxAvatarItem
												key={account.id}
												value={account}
												avatarImageUrl={''}
												avatarFallbackText={account.companyName?.substring(0, 2).toUpperCase()}
												title={account.companyName}
											/>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						)}
					</div>

					{/* Meeting Note Content */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>
							Meeting Notes <span className={css(styleSheet.required)}>*</span>
						</label>
						<div className={`${css(styleSheet.richEditorWrapper)} ${MEETING_NOTE_EDITOR_CLASS}`}>
							<RichContentDocumentEditor
								contentState={meetingNoteContentState}
								onContentStateChanged={onContentStateChanged}
								readOnly={isSavingNote}
								config={{
									autoresizeToFitContent: true,
									minHeight: 300,
									contentHorizontalPadding: 20,
									toolbar: 'undo redo | bold italic underline | bullist numlist',
									contentRawCss: EditorCSS,
								}}
							/>
						</div>
					</div>
				</div>

				{/* Right Column */}
				<div className={css(styleSheet.formRightColumn)}>
					{/* Meeting Type */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>
							Meeting Type <span className={css(styleSheet.required)}>*</span>
						</label>
						<Combobox
							items={Object.values(CsmMeetingTypes)}
							value={meetingType}
							onValueChange={value => onMeetingTypeChange(value as CsmMeetingTypes)}
						>
							<ComboboxInput placeholder='Search meeting type...' showClear />
							<ComboboxContent>
								<ComboboxEmpty>No meeting types found.</ComboboxEmpty>
								<ComboboxList>
									{(type: string) => (
										<ComboboxItem key={type} value={type}>
											<ComboboxItemRadioIndicator />
											{type}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>

					{/* Onboarding Checkbox */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.checkboxLabel)}>
							<input type='checkbox' checked={isOnboarding} onChange={e => onIsOnboardingChange(e.target.checked)} />
							<span className={css(styleSheet.onboardingText)}>This was part of the onboarding process</span>
						</label>
					</div>

					{/* Meeting Status */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>Meeting Status</label>
						<Select value={meetingStatus} onValueChange={onMeetingStatusChange}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Completed'>Completed</SelectItem>
								<SelectItem value='No showed'>No showed</SelectItem>
								<SelectItem value='Rescheduled'>Rescheduled</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{/* Account Stage */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>Account Stage</label>
						<Combobox
							items={NormalizedStages}
							value={accountStage}
							onValueChange={value => onAccountStageChange(value as string | null)}
						>
							<ComboboxInput placeholder='Select account stage...' showClear />
							<ComboboxContent>
								<ComboboxEmpty>No stages found.</ComboboxEmpty>
								<ComboboxList>
									{(stage: string) => (
										<ComboboxItem key={stage} value={stage}>
											<ComboboxItemRadioIndicator />
											{stage}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>

					{/* Duration */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>Duration (minutes)</label>
						<Select value={duration} onValueChange={onDurationChange}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{[15, 30, 45, 60, 90, 120].map(mins => (
									<SelectItem key={mins} value={mins}>
										{mins} minutes
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Next Meeting Date */}
					<div className={css(styleSheet.formField)}>
						<label className={css(styleSheet.formLabel)}>Next Meeting Date</label>
						<div className={css(styleSheet.nextMeetingContainer)}>
							<button
								type='button'
								className={css(styleSheet.nextMeetingButton)}
								onClick={() => onShowNextMeetingDatePickerChange(!showNextMeetingDatePicker)}
							>
								{nextMeetingDate
									? nextMeetingDate.toLocaleDateString('en-US', {
											weekday: 'short',
											year: 'numeric',
											month: 'short',
											day: 'numeric',
										})
									: 'Select date...'}
							</button>
							{nextMeetingDate && (
								<button
									type='button'
									className={css(styleSheet.clearDateButton)}
									onClick={() => onNextMeetingDateChange(null)}
								>
									Clear
								</button>
							)}
						</div>
						{showNextMeetingDatePicker && (
							<div className={css(styleSheet.datePickerContainer)}>
								<DayPicker
									allowPastDates={false}
									onDayClick={day => {
										onNextMeetingDateChange(day);
										onShowNextMeetingDatePickerChange(false);
									}}
									selectedDays={nextMeetingDate || undefined}
								/>
							</div>
						)}
					</div>

					{/* Action Buttons */}
					<div className={css(styleSheet.formActions)}>
						<button className={css(styleSheet.cancelButton)} onClick={onSkip} disabled={isSavingNote}>
							Skip
						</button>
						<button className={css(styleSheet.saveButton)} onClick={onSave} disabled={isSavingNote}>
							{isSavingNote ? 'Saving...' : 'Save Meeting Note'}
						</button>
					</div>
					{/* CC notice for Implementation team meetings */}
					{csmUserForCcNotice && (
						<div className={css(styleSheet.ccNotice)}>
							We&apos;ll automatically cc {getDisplayName(csmUserForCcNotice)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
